import { ViewingsDispatchInspector, ViewingsDispatchRequest } from "@/schemas";
import { IntlShape } from "react-intl";
import { CustomColumnDef } from "@/components/DataTable/DataTable";
import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getLifecycleBadgeClassName } from "./dispatchActions";
import {
  getInspectionTypeLabel,
  getLifecycleStatusLabel,
} from "./viewingLabels";
import {
  shouldShowWaitingOver48hAlert,
  WaitingOver48hAlertIndicator,
} from "./components/WaitingOver48hAlert";

const OFFER_OPEN_STATUSES = ["contacted", "suggested"];

type ViewingColumnActions = {
  activeInspector?: ViewingsDispatchInspector;
  onRespond?: (request: ViewingsDispatchRequest) => void;
};

export function getViewingColumns(
  intl: IntlShape,
  actions: ViewingColumnActions = {},
): CustomColumnDef<ViewingsDispatchRequest>[] {
  return [
    {
      accessorKey: "evalionId",
      header: intl.formatMessage({ id: "viewings.table.evalionId" }),
      canSort: true,
      cell: ({ row }) => (
        <span className="inline-flex items-center gap-2 font-semibold text-primary">
          <span>{row.original.evalionId}</span>
          {shouldShowWaitingOver48hAlert(row.original) ? (
            <WaitingOver48hAlertIndicator />
          ) : null}
        </span>
      ),
    },
    {
      accessorKey: "addressLine",
      header: intl.formatMessage({ id: "viewings.table.address" }),
      canSort: true,
      cellClassName: "min-w-72 font-medium text-primary",
    },
    {
      accessorKey: "lifecycleStatus",
      header: intl.formatMessage({ id: "viewings.table.status" }),
      canSort: true,
      cell: ({ row }) => (
        <span
          className={getLifecycleBadgeClassName(row.original.lifecycleStatus)}
        >
          {getLifecycleStatusLabel(intl, row.original.lifecycleStatus)}
        </span>
      ),
    },
    {
      accessorKey: "deadlineDate",
      header: intl.formatMessage({ id: "viewings.table.deadline" }),
      canSort: true,
    },
    {
      accessorKey: "inspectionType",
      header: intl.formatMessage({ id: "viewings.table.inspection" }),
      cell: ({ row }) =>
        row.original.inspectionType
          ? getInspectionTypeLabel(intl, row.original.inspectionType)
          : "-",
    },
    {
      accessorKey: "productType",
      header: intl.formatMessage({ id: "viewings.table.product" }),
      cell: ({ row }) => row.original.productType || "-",
    },
    {
      accessorKey: "distanceKm",
      header: intl.formatMessage({ id: "viewings.table.distance" }),
      canSort: true,
      cell: ({ row }) =>
        typeof row.original.distanceKm === "number"
          ? `${row.original.distanceKm.toFixed(1)} km`
          : "-",
    },
    {
      id: "actions",
      header: intl.formatMessage({ id: "viewings.table.actions" }),
      cell: ({ row }) => {
        const request = row.original;
        const isAssignedToCurrentInspector =
          request.assignedInspectorId === actions.activeInspector?.id;
        const canRespond =
          isAssignedToCurrentInspector &&
          !request.outreach?.isAssignmentInvalidated &&
          OFFER_OPEN_STATUSES.includes(request.lifecycleStatus);

        if (!actions.onRespond) {
          return <ChevronRight className="h-4 w-4 text-muted-foreground" />;
        }

        return (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs font-medium text-muted-foreground hover:text-primary"
            onClick={(event) => {
              event.stopPropagation();
              actions.onRespond?.(request);
            }}
          >
            {intl.formatMessage({
              id: canRespond ? "actions.respond" : "actions.viewDetails",
            })}
          </Button>
        );
      },
      cellClassName: "w-24 text-right",
    },
  ];
}
